// services/auth.service.jsx
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Helper from './helper.service';
import * as _ from 'lodash';

export const API_URL = process.env.REACT_APP_API_URL + "api/public/";
export const IMG_URL = process.env.REACT_APP_IMG_URL + "uploads/artbanne/";

const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('jwtToken') || null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const DELAY_RETRY_ON_REQUESTED = 1000;
    const MAX_RETRIES = 3;

    // Stable fetchUser
    const fetchUser = useCallback(async (retry = 0) => {
        if (!token) {
            setUser(null);
            return null;
        }
        try {
            const res = await axios.get(`${API_URL}login`, { headers: { 'x-access-token': token } });
            setUser(res.data);
            return res.data;
        } catch (err) {
            if (retry < MAX_RETRIES) {
                await new Promise(r => setTimeout(r, DELAY_RETRY_ON_REQUESTED));
                return fetchUser(retry + 1);
            }
            localStorage.removeItem('jwtToken');
            setToken(null);
            setUser(null);
            return null;
        }
    }, [token]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            await fetchUser();
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [fetchUser]);

    // ⬇️ Memoized functions (fixes eslint warnings)
    const login = useCallback(async (credentials) => {
        try {
            const res = await axios.post(API_URL + 'login', credentials);
            Helper.toast("Connexion réussie", "success");
            const tk = res.data.token;
            localStorage.setItem('jwtToken', tk);
            setToken(tk);
            setUser(res.data.user);
            // Do NOT navigate here; caller decides where to go
            return { ok: true, data: res.data };
        } catch (error) {
            Helper.toast(_.get(error, 'response.data', "Identifiants invalides"), "error");
            console.error('Login error:', error);
            return { ok: false, error };
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('jwtToken');
        setToken(null);
        setUser(null);
        navigate('/login', { replace: true });
    }, [navigate]);

    const hasRole = useCallback((role) => {
        const roles = _.get(user, 'roles', []);
        return Array.isArray(roles) && roles.includes(role);
    }, [user]);

    const hasPerm = useCallback((perm) => {
        const perms = _.get(user, 'permissions', []);
        return Array.isArray(perms) && perms.includes(perm);
    }, [user]);

    const canAdmin = useMemo(() => !!user && hasRole('Dev'), [user, hasRole]);

    const value = useMemo(() => ({
        user,
        setUser,          // state setter is stable, safe to expose
        login,
        logout,
        canAdmin,
        hasRole,
        hasPerm,
        loading,
    }), [user, login, logout, canAdmin, hasRole, hasPerm, loading]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};